import { getEffectiveSeriesColor } from "entities/charts/eva/translators/getEffectiveSeriesColor";
import {
  historyAxisDates,
  isMonthEndHistoryAxis
} from "entities/charts/eva/translators/monthEndHistory";
import { tooltipColumnRow } from "entities/charts/eva/translators/rigFracTooltip";
import {
  getGroupNameFromLabel,
  isGapBridgeId,
  isGapJoinEnabled
} from "entities/charts/eva/translators/utils";
import { MultiProductFeature } from "types/charts/feature";
import { WellCountsTooltipOptions } from "types/charts/wellCounts";
import { removeCount } from "utils";

import { DataType } from "models";
import { ChartLayout } from "models/chart";
import { ChartSeries } from "models/model";

import {
  getPrecision,
  getSeriesNameWithFormattedUwi,
  getSeriesTooltipDashedLine,
  getSeriesTooltipDiamond,
  getSeriesTooltipDot,
  getUnit
} from "components/charts/utils";
import { formatNumber } from "components/charts/utils/precisionUtils";
import { DEFAULT_SIGNIFICANT_FIGURES } from "components/user-settings/models/SignificantFiguresSettings";

const MULTI_PRODUCT_MULTI_SERIES_TOOLTIP_PRECISION = 4;

/**
 * Returns an HTML line showing the resolved forecast source for a well when
 * forecast trumping is active. Returns empty string when no source is available.
 */
export function getForecastSourceTooltipLine(
  uwiCoordinates: object | undefined,
  uwi: string
): string {
  if (!uwiCoordinates || !uwi) return "";
  const forecastSource = (uwiCoordinates as Record<string, { forecastSource?: string }>)[
    uwi
  ]?.forecastSource;
  if (!forecastSource) return "";
  return `<br/><span style="color:#666;">Fcst Source: ${forecastSource}</span>`;
}

export function formatSingleSeriesTooltip(
  series,
  yAxisTitle: string,
  colour: string,
  spaghettiEnabled: boolean = false,
  precisionOverride?: number
): string {
  const value = Array.isArray(series.value) ? series.value[1] : series.value;

  let formattedValue =
    value !== "" && yAxisTitle
      ? value.toFixed(precisionOverride ?? getPrecision(getUnit(yAxisTitle)))
      : value;

  if (
    series.seriesName.toLowerCase() === "well count" ||
    series.seriesName.toLowerCase() === "facility count"
  ) {
    formattedValue = Math.round(value).toString();
  }

  let seriesName = series.seriesName;
  if (spaghettiEnabled) {
    seriesName = getSeriesNameWithFormattedUwi(series.seriesName);
  }

  return `<p style="text-align:right;">${getSeriesTooltipDot(
    colour
  )} ${seriesName}:&emsp;<span style="font-weight:700;color:#666;font-family: var(--fontMono);">${formattedValue}</span></p>`;
}

export function formatMultiSeriesTooltip(
  items,
  yAxisTitle: string,
  precisionOverride?: number
): string {
  const header = `<table>`;
  const footer = `</table>`;

  const content = items
    .filter((item) => item.value !== "")
    .sort((a, b) =>
      (Array.isArray(a.value) ? a.value[1] : a.value) >
      (Array.isArray(b.value) ? b.value[1] : b.value)
        ? -1
        : 1
    )
    .map((item) => {
      const value = Array.isArray(item.value) ? item.value[1] : item.value;
      if (typeof value !== "number" || isNaN(value)) {
        return "";
      }

      const formattedValue =
        item.seriesName !== "Well Count" && item.seriesName !== "Facility Count"
          ? value.toFixed(precisionOverride ?? getPrecision(getUnit(yAxisTitle)))
          : value;

      return `<tr style="text-align:left;">
          <td>${getSeriesTooltipDot(item.color)}</td>
          <td>${item.seriesName}</td>
          <td><span style="font-weight:700;color:#666;padding-left:10px;float:right;text-align:right;font-family: var(--fontMono);">${formattedValue}</span></td>
        </tr>`;
    })
    .join("");

  return `${header}${content}${footer}`;
}
export function formatBarTooltip(
  series,
  yAxisTitle: string,
  showHistogram: boolean,
  defaultSignificantFigures: number = DEFAULT_SIGNIFICANT_FIGURES,
  useStrictRounding: boolean = true,
  yAxisDataType?: DataType
): string {
  if (showHistogram) {
    // Reformat the tooltip since history only have 1 series per column
    const selectedItem = series.find(
      (item) => item.value !== "" && item.value !== 0 && item.value[0] !== 0
    );

    if (selectedItem) {
      const value = Array.isArray(selectedItem.value)
        ? selectedItem.value[1]
        : selectedItem.value;

      return (
        `<span style="color:#666";>${getSeriesTooltipDot(selectedItem.color)}${
          selectedItem.seriesName
        }</span>` +
        `<p">&emsp;<span style="font-weight:700;color:#666;padding-left:10px;float:right;text-align:right;font-family: var(--fontMono);">${formatNumber(
          value,
          defaultSignificantFigures,
          useStrictRounding,
          yAxisDataType
        )}</span></p>`
      );
    }

    return "";
  } else {
    const header = `<table>`;
    const footer = `</table>`;

    let content = "";
    let total = 0;
    series
      .filter((item) => item?.data !== "")
      .forEach((item) => {
        const value = Array.isArray(item.value) ? item.value[1] : item.value;
        total += value;
        content += `<tr style="text-align:left;">
              <td>${getSeriesTooltipDot(item.color)}</td>
              <td>${item.seriesName}</td>
              <td><span style="font-weight:700;color:#666;padding-left:10px;float:right;text-align:right;font-family: var(--fontMono);">${formatNumber(
                value,
                defaultSignificantFigures,
                useStrictRounding,
                yAxisDataType
              )}</span></td>
              </tr>`;
      });
    // The top row showing the group and the total for the stack.
    const headerRow = `<tr>
                  <th colspan="2"><span style="display:block;text-align:left">${
                    series && series.length > 0 ? series[0].axisValueLabel : ""
                  }</span></th>
                  <td><span style="font-weight:700;color:#666;padding-left:10px;float:right;text-align:right;font-family: var(--fontMono);">${formatNumber(
                    total,
                    defaultSignificantFigures,
                    useStrictRounding,
                    yAxisDataType
                  )}</span></td>
                  </tr>`;
    return `${header}${headerRow}${content}${footer}`;
  }
}

export function formatBoxPlotTooltip(
  series,
  defaultSignificantFigures: number = DEFAULT_SIGNIFICANT_FIGURES,
  useStrictRounding: boolean = true,
  uwiCoordinates?: object,
  yAxisDataType?: DataType
): string {
  const data = series?.data;
  const color = series?.color;
  const name = series?.name;
  const seriesName = series?.seriesName;
  const componentType = series?.componentType;

  // Handle markLine tooltip (type well horizontal lines in non-jitter mode)
  if (componentType === "markLine") {
    const markLineData = series?.data;
    const markLineName = markLineData?.name ?? name;
    const yValue = markLineData?.value ?? markLineData?.yAxis;
    const lineColor = series?.borderColor ?? color;
    return (
      `<div style="line-height:1.4; padding:2px 3px;">` +
      `<span style="color:#666; font-weight: 600;">Type Well</span>` +
      `<br/>` +
      `<span style="display:inline; color:#666;">${getSeriesTooltipDashedLine(
        lineColor
      )}${markLineName}</span>` +
      `<br/>` +
      `<span style="font-size:14px;font-weight:700;color:#666;font-family: var(--fontMono);">${
        typeof yValue === "number"
          ? formatNumber(
              yValue,
              defaultSignificantFigures,
              useStrictRounding,
              yAxisDataType
            )
          : yValue
      }</span>` +
      `</div>`
    );
  }

  if (!data) {
    return "";
  }
  if (Array.isArray(data) && data.length > 4) {
    // If data is an array, this is a jitter plot
    // data[3] is the UWI key from uwiCoordinates
    const uwiKey = typeof data[3] === "string" ? data[3] : undefined;
    const forecastSourceLine = uwiKey
      ? getForecastSourceTooltipLine(uwiCoordinates, uwiKey)
      : "";
    return (
      `<div style="line-height:1.2; padding:2px 3px;">` +
      `<span style="display:inline;">${getSeriesTooltipDot(data?.[6])}${
        data[0]
      } </span>` +
      `<span style="font-size:14px;font-weight:700;color:#666;text-align:right;font-family: var(--fontMono); margin-left:10px;">  ${getSeriesNameWithFormattedUwi(
        typeof data[1] === "number"
          ? formatNumber(
              data[1],
              defaultSignificantFigures,
              useStrictRounding,
              yAxisDataType
            )
          : data[1]
      )}</span>` +
      forecastSourceLine +
      `</div>`
    );
  }
  // Handle type well scatter series in jitter mode (data is [binCategory, yValue])
  if (Array.isArray(data) && data.length === 2 && typeof data[1] === "number") {
    const binCategory = data[0];
    const yValue = data[1];
    return (
      `<div style="line-height:1.4; padding:2px 3px;">` +
      `<span style="display:inline;">${getSeriesTooltipDiamond(
        color
      )}${seriesName}</span>` +
      `<br/>` +
      `<span style="display:inline; color:#666;">${binCategory}</span>` +
      `<span style="font-size:14px;font-weight:700;color:#666;text-align:right;font-family: var(--fontMono); margin-left:10px;">${
        typeof yValue === "number"
          ? formatNumber(
              yValue,
              defaultSignificantFigures,
              useStrictRounding,
              yAxisDataType
            )
          : yValue
      }</span>` +
      `</div>`
    );
  }
  const header = `<p style="text-align:left; margin-bottom:6px; color:#666; margin-left:-2px">${getSeriesTooltipDot(
    color
  )} ${name}</p><table style="width:100%;">`;
  const rowContent = (label, data) =>
    `<tr>
      <td style="text-align:left;">
        ${label}
      </td>
      <td style="font-weight:700;color:#666;text-align:right;font-family: var(--fontMono);">${
        // stats are unaffected by integer data type
        typeof data === "number"
          ? formatNumber(data, defaultSignificantFigures, useStrictRounding)
          : typeof data === "string" && !isNaN(Number(data))
          ? formatNumber(Number(data), defaultSignificantFigures, useStrictRounding)
          : data
      }</td>
    </tr>`;

  const footer = `</table>`;
  //note: 0th index here contains the index of the series
  const value = data.value;
  // Guard against undefined value (e.g., for scatter series)
  if (!value || !Array.isArray(value)) {
    return `${header}${footer}`;
  }
  const body =
    value.length > 8
      ? [
          rowContent("Max: ", value[5]),
          rowContent("10th: ", value[8]),
          rowContent("Q1: ", value[4]),
          rowContent("Mean: ", value[6]),
          rowContent("Median: ", value[3]),
          rowContent("Q3: ", value[2]),
          rowContent("90th: ", value[7]),
          rowContent("Min: ", value[1])
        ].join("")
      : "";
  return `${header}${body}${footer}`;
}

interface WellCountsAtPoint {
  total: number;
  producing: number;
  forecast: number;
  // Null from a chart-svc that predates the field: the padded wells are then the remainder.
  survivorBias: Nullable<number>;
}

/** The parts of an ECharts tooltip item the well-count rows read. */
interface HoveredPoint {
  value?: unknown;
  dataIndex?: number;
  seriesName?: string;
}

const resolveSeriesYAxisTitle = (
  series: Maybe<ChartSeries>,
  layout: Maybe<ChartLayout>,
  yAxisTitle: string
): string => {
  const yAxisIndex = series?.yAxisIndex;
  return layout?.yAxis2 != null && typeof yAxisIndex === "number"
    ? layout.yAxis2[yAxisIndex]?.title ?? yAxisTitle
    : yAxisTitle;
};

/**
 * Counts for the hovered point. Curves can share a label (Rate Time compare-mode
 * histories), so take the one holding this exact point; a point that doesn't line up
 * with its series gets no counts rather than a neighbour's.
 */
export function getWellCountsAt(
  series: ChartSeries[],
  item: Maybe<HoveredPoint>,
  // Total Rate vs Date series carry no x; their points sit on the layout's shared dates.
  categoryX?: (string | number)[],
  // With the join on, their history is drawn on the month-end form of those dates (EVA-389).
  historyCategoryX?: (string | number)[]
): Maybe<WellCountsAtPoint> {
  if (!Array.isArray(item?.value) || item.dataIndex == null) {
    return null;
  }
  const [x, y] = item.value;
  const index = item.dataIndex;
  const candidates = series.filter(
    (s) =>
      s.label === item.seriesName &&
      ((categoryX ?? s.x)?.[index] === x || historyCategoryX?.[index] === x)
  );
  const counts = (candidates.find((s) => s.y?.[index] === y) ?? candidates[0])
    ?.wellCounts;
  if (!counts || index >= counts.total.length) {
    return null;
  }
  return {
    total: counts.total[index],
    producing: counts.producing[index],
    forecast: counts.forecast[index],
    survivorBias: counts.survivorBias?.[index] ?? null
  };
}

export function formatWellCountsTooltip(
  item: HoveredPoint,
  yAxisTitle: string,
  counts: WellCountsAtPoint,
  options: WellCountsTooltipOptions = {},
  precisionOverride?: number
): string {
  const value = Array.isArray(item.value) ? item.value[1] : item.value;
  const rate =
    typeof value === "number" && yAxisTitle
      ? value.toFixed(precisionOverride ?? getPrecision(getUnit(yAxisTitle)))
      : String(value);
  const forecast = !options.survivorBiasAsForecast
    ? counts.forecast
    : counts.survivorBias == null
    ? counts.total - counts.producing
    : counts.forecast + counts.survivorBias;
  const entityLabel = options.entityLabel ?? "Wells";
  // The series name, not just its group, so history and forecast curves stay distinguishable,
  // without the legend's "(N) " group size, which reads like a second Total Wells.
  const rows: [string, string][] = [
    [options.groupByTitle || "Group", removeCount(item.seriesName ?? "")],
    [yAxisTitle || "Rate", rate],
    [`Total ${entityLabel}`, counts.total.toLocaleString("en-US")],
    [`Producing ${entityLabel}`, counts.producing.toLocaleString("en-US")],
    [`Forecasted ${entityLabel}`, forecast.toLocaleString("en-US")]
  ];
  // Label and value columns, as in the EVA-353 mockup.
  return `<table>${rows
    .map(([label, cell]) => tooltipColumnRow(label, cell))
    .join("")}</table>`;
}

export function formatLineChartTooltip(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  items: any,
  hoverLegendItem: string | null,
  series: ChartSeries[],
  yAxisTitle: string,
  spaghettiEnabled: boolean = false,
  layout?: ChartLayout,
  isMultiProductActive?: boolean,
  multiProduct?: Maybe<Partial<MultiProductFeature>>,
  wellCountsOptions?: WellCountsTooltipOptions
): string {
  const multiProductPrecision = isMultiProductActive
    ? MULTI_PRODUCT_MULTI_SERIES_TOOLTIP_PRECISION
    : undefined;
  if (hoverLegendItem && items) {
    const hoveredSeries = Array.isArray(items)
      ? items.filter((item) => item.seriesName.startsWith(hoverLegendItem))[0]
      : items;

    if (hoveredSeries) {
      const originalSeries = series.find((s) => s.label === hoveredSeries?.seriesName);
      const seriesColour =
        hoveredSeries.seriesIndex >= series.length // when hovered series index is >= the series from response, this is typewell series
          ? hoveredSeries.color
          : getEffectiveSeriesColor(originalSeries, multiProduct);

      const resolvedYAxisTitle = resolveSeriesYAxisTitle(
        originalSeries,
        layout,
        yAxisTitle
      );

      // chart-svc sends no counts on individual wells; Average and P10/P50/P90 keep theirs.
      const counts = getWellCountsAt(series, hoveredSeries);
      if (counts) {
        return formatWellCountsTooltip(
          hoveredSeries,
          resolvedYAxisTitle,
          counts,
          wellCountsOptions
        );
      }

      return formatSingleSeriesTooltip(
        hoveredSeries,
        resolvedYAxisTitle,
        seriesColour,
        spaghettiEnabled
      );
    }
    return "";
  }

  if (items && Array.isArray(items) && items.length === 1) {
    const counts = getWellCountsAt(series, items[0]);
    if (counts) {
      const originalSeries = series.find((s) => s.label === items[0].seriesName);
      return formatWellCountsTooltip(
        items[0],
        resolveSeriesYAxisTitle(originalSeries, layout, yAxisTitle),
        counts,
        wellCountsOptions,
        multiProductPrecision
      );
    }

    return formatSingleSeriesTooltip(
      items[0],
      yAxisTitle,
      items[0].color,
      spaghettiEnabled,
      multiProductPrecision
    );
  }

  if (items && items.length > 0) {
    return formatMultiSeriesTooltip(items, yAxisTitle, multiProductPrecision);
  }

  return "";
}

const hasTooltipValue = (item: { value?: unknown }): boolean => {
  const value = Array.isArray(item.value) ? item.value[1] : item.value;
  return value !== "" && value != null;
};

export function formatTotalRateDateChartTooltip(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  allItems: any,
  hoverLegendId: string | null,
  series: ChartSeries[],
  yAxisTitle: string,
  spaghettiEnabled: boolean = false,
  precisionOverride?: number,
  wellCountsOptions?: WellCountsTooltipOptions,
  categoryX?: (string | number)[]
): string {
  // Gap bridges repeat their group's boundary values; keep them out of the text.
  const items = Array.isArray(allItems)
    ? allItems.filter((item) => !isGapBridgeId(item.seriesId))
    : allItems;
  const historyX =
    categoryX &&
    historyAxisDates(categoryX, isMonthEndHistoryAxis(categoryX, isGapJoinEnabled()));
  const formatWithWellCounts = (item: HoveredPoint): Maybe<string> => {
    const counts = hasTooltipValue(item)
      ? getWellCountsAt(series, item, categoryX, historyX)
      : null;
    return counts
      ? formatWellCountsTooltip(
          item,
          yAxisTitle,
          counts,
          wellCountsOptions,
          precisionOverride
        )
      : null;
  };
  if (hoverLegendId && items) {
    const matches = Array.isArray(items)
      ? items.filter((item) => item.seriesName.startsWith(hoverLegendId))
      : [items];
    // At a join's forecast end the history item is empty; show its group's item with a value.
    const group = getGroupNameFromLabel(hoverLegendId);
    const hoveredSeries =
      (isGapJoinEnabled() &&
        matches.find(
          (item) =>
            hasTooltipValue(item) && getGroupNameFromLabel(item.seriesName) === group
        )) ||
      matches[0];
    if (hoveredSeries) {
      const withCounts = formatWithWellCounts(hoveredSeries);
      if (withCounts) {
        return withCounts;
      }
      const seriesColour =
        hoveredSeries.seriesIndex >= series.length // when hovered series index is >= the series from response, this is typewell series
          ? hoveredSeries.color
          : series.find((s) => s.label === hoveredSeries?.seriesName)?.style?.hexColor;

      return formatSingleSeriesTooltip(
        hoveredSeries,
        yAxisTitle,
        seriesColour,
        spaghettiEnabled,
        precisionOverride
      );
    }
    return "";
  }

  if (items && Array.isArray(items) && items.length === 1) {
    return (
      formatWithWellCounts(items[0]) ??
      formatSingleSeriesTooltip(
        items[0],
        yAxisTitle,
        items[0].color,
        spaghettiEnabled,
        precisionOverride
      )
    );
  }

  if (items && items.length > 0) {
    return formatMultiSeriesTooltip(items, yAxisTitle, precisionOverride);
  }

  return "";
}

export function getTooltipPositionWithXFieldSelector(point, params, dom, rect, size) {
  let x = point[0];
  let y = point[1];
  const viewWidth = size.viewSize[0];
  const viewHeight = size.viewSize[1];
  const boxWidth = size.contentSize[0];
  const boxHeight = size.contentSize[1];

  // avoid showing tooltip under field selector
  const bottomThreshold = 20;

  // Adjust x/y so tooltip stays within chart area
  if (x + boxWidth > viewWidth) {
    x -= boxWidth;
  }
  if (y + boxHeight > viewHeight - bottomThreshold) {
    y -= boxHeight;
  }
  return [x, y];
}

export function formatSingleSeriesWithSigFigsTooltip(
  series,
  yAxisTitle: string,
  colour: string,
  spaghettiEnabled: boolean = false,
  defaultSignificantFigures: number = DEFAULT_SIGNIFICANT_FIGURES,
  useStrictRounding: boolean = true,
  yAxisDataType?: DataType
): string {
  const value = Array.isArray(series.value) ? series.value[1] : series.value;

  let formattedValue =
    value !== "" && yAxisTitle
      ? formatNumber(value, defaultSignificantFigures, useStrictRounding, yAxisDataType)
      : value;

  if (
    series.seriesName.toLowerCase() === "well count" ||
    series.seriesName.toLowerCase() === "facility count" ||
    series.seriesName.toLowerCase() === "injector count" ||
    yAxisTitle?.toLowerCase() === "well count" ||
    yAxisTitle?.toLowerCase() === "facility count" ||
    yAxisTitle?.toLowerCase() === "injector count"
  ) {
    formattedValue = Math.round(value).toString();
  }

  let seriesName = series.seriesName;
  if (spaghettiEnabled) {
    seriesName = getSeriesNameWithFormattedUwi(series.seriesName);
  }

  return `<p style="text-align:right;">${getSeriesTooltipDot(
    colour
  )} ${seriesName}:&emsp;<span style="font-weight:700;color:#666;font-family: var(--fontMono);">${formattedValue}</span></p>`;
}

export function formatMultiSeriesWithSigFigsTooltip(
  items,
  yAxisTitle: string,
  defaultSignificantFigures: number = DEFAULT_SIGNIFICANT_FIGURES,
  useStrictRounding: boolean = true,
  yAxisDataType?: DataType
): string {
  const header = `<table>`;
  const footer = `</table>`;

  const content = items
    .filter((item) => item.value !== "")
    .sort((a, b) =>
      (Array.isArray(a.value) ? a.value[1] : a.value) >
      (Array.isArray(b.value) ? b.value[1] : b.value)
        ? -1
        : 1
    )
    .map((item) => {
      const value = Array.isArray(item.value) ? item.value[1] : item.value;
      if (typeof value !== "number" || isNaN(value)) {
        return "";
      }

      const isCountSeries =
        item.seriesName === "Well Count" ||
        item.seriesName === "Facility Count" ||
        item.seriesName === "Injector Count" ||
        yAxisTitle?.toLowerCase() === "well count" ||
        yAxisTitle?.toLowerCase() === "facility count" ||
        yAxisTitle?.toLowerCase() === "injector count";

      const formattedValue = isCountSeries
        ? Math.round(value).toString()
        : formatNumber(
            value,
            defaultSignificantFigures,
            useStrictRounding,
            yAxisDataType
          );

      return `<tr style="text-align:left;">
          <td>${getSeriesTooltipDot(item.color)}</td>
          <td>${item.seriesName}</td>
          <td><span style="font-weight:700;color:#666;padding-left:10px;float:right;text-align:right;font-family: var(--fontMono);">${formattedValue}</span></td>
        </tr>`;
    })
    .join("");

  return `${header}${content}${footer}`;
}

export function formatLineChartTooltipWithSigFigs(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  items: any,
  hoverLegendItem: string | null,
  series: ChartSeries[],
  yAxisTitle: string,
  spaghettiEnabled: boolean = false,
  defaultSignificantFigures: number = DEFAULT_SIGNIFICANT_FIGURES,
  useStrictRounding: boolean = true,
  yAxisDataType?: DataType
): string {
  if (hoverLegendItem && items) {
    const hoveredSeries = Array.isArray(items)
      ? items.filter((item) => item.seriesName.startsWith(hoverLegendItem))[0]
      : items;

    if (hoveredSeries) {
      const seriesColour =
        hoveredSeries.seriesIndex >= series.length // when hovered series index is >= the series from response, this is typewell series
          ? hoveredSeries.color
          : series.find((s) => s.label === hoveredSeries?.seriesName)?.style?.hexColor;

      return formatSingleSeriesWithSigFigsTooltip(
        hoveredSeries,
        yAxisTitle,
        seriesColour,
        spaghettiEnabled,
        defaultSignificantFigures,
        useStrictRounding,
        yAxisDataType
      );
    }
    return "";
  }

  if (items && Array.isArray(items) && items.length === 1) {
    return formatSingleSeriesWithSigFigsTooltip(
      items[0],
      yAxisTitle,
      items[0].color,
      spaghettiEnabled,
      defaultSignificantFigures,
      useStrictRounding,
      yAxisDataType
    );
  }

  if (items && items.length > 0) {
    return formatMultiSeriesWithSigFigsTooltip(
      items,
      yAxisTitle,
      defaultSignificantFigures,
      useStrictRounding,
      yAxisDataType
    );
  }

  return "";
}
